'use client';

import './styles/@theme';

import { useMemo } from 'react';
import { getText } from '@/utils/text/text';
import { ValueTicker } from '@/app/elements/value-ticker/value-ticker';
import { SvgLoader } from '@/app/elements/svg/loader';
import { useStore } from '@/store/store';
import type { MultiplierFormat } from '@/app/components/site-wide-jackpot/transformers/site-wide-jackpot.transformer';

type AnimatedTopBarProps = {
    multipliers?: MultiplierFormat[];
    multiplier?: number;
    wageredAmount?: number;
    boosted?: boolean;
    shortWagerLabel?: string;
};

export const AnimatedTopBar = ({
    multipliers,
    multiplier,
    wageredAmount,
    boosted,
    shortWagerLabel
}: AnimatedTopBarProps) => {
    const { siteWideJackpot } = useStore();

    const topBarText = useMemo(
        () => multipliers?.find((item) => item.multiplier === (multiplier ?? 0))?.tickerPillLobbyText ?? '',
        [multipliers, multiplier]
    );

    if (siteWideJackpot.loading || siteWideJackpot.updatedTiers.length === 0) {
        return <div className="loading-state">{getText({ text: 'components.siteWideJackpots.loadingStatus' })}</div>;
    }

    const values = siteWideJackpot.updatedTiers.map((tier) => ({
        id: tier.tier_id,
        currentAmount: tier.currentAmount,
        prevAmount: tier.prevAmount,
        logoUrl: tier.logo1?.url || '' // Fallback to empty string
    }));

    return (
        <div className="animated-container">
            <div className="tier-logos">
                <div className="tier-logos-list" style={{ animationDuration: `${(values.length + 1) * 3}s` }}>
                    {[...values, values[0]].map((value, index) => (
                        <img
                            key={index === values.length ? `clone-${value.id}` : value.id}
                            src={value.logoUrl}
                            alt="Logo"
                            className="tier-logos-list-item"
                        />
                    ))}
                </div>
            </div>

            <div className="tier-values">
                <div className="tier-values-container">
                    <div className="tier-values-list" style={{ animationDuration: `${(values.length + 1) * 3}s` }}>
                        {[...values, values[0]].map((value, index) => (
                            <div
                                key={index === values.length ? `clone-${value.id}` : value.id}
                                className="tier-values-list-item">
                                <ValueTicker
                                    prevAmount={Number(value?.prevAmount)}
                                    amount={Number(value?.currentAmount)}
                                />
                            </div>
                        ))}
                    </div>
                </div>
                <div className="tier-multiplier">
                    {multiplier === 0 ? (
                        <div>{topBarText}</div>
                    ) : (
                        <>
                            {topBarText}
                            <strong>${wageredAmount?.toFixed(2)}</strong>
                            {boosted && (
                                <span className="tier-multiplier-boosted">
                                    <span className="tier-multiplier-boosted-label">{shortWagerLabel}</span>
                                    <SvgLoader
                                        iconName="site-wide-jackpot-bolt"
                                        directory="swj-icons"
                                        fill="purple"
                                        width={7}
                                        height={10}
                                    />
                                </span>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
